/* eslint-disable no-undef */
import moment from 'moment'
import { PreviewImg } from './imagepath'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import localizedFormat from 'dayjs/plugin/localizedFormat' // For more flexible formats
import timezone from 'dayjs/plugin/timezone'
import axios from 'axios'
import { permissions } from '../util/roleConstant'

// Extend dayjs with UTC and localized format plugins
dayjs.extend(utc)
dayjs.extend(localizedFormat)
dayjs.extend(timezone)

export const amountFormat = (value) => {
  return (value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export const toTitleCase = (str) => {
  if (typeof str !== 'string' || str.trim() === '') {
    return str
  }

  return str.toLowerCase().replace(/(?:^|\s)\w/g, function (match) {
    return match.toUpperCase()
  })
}

export const formattedTime = (date) => {
  return moment(date).format('h:mm A')
}

export const handleImageError = (event) => {
  event.target.src = PreviewImg
}

export const uniqueId = () => {
  var dt = new Date().getTime()
  var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (dt + Math.random() * 16) % 16 | 0
    dt = Math.floor(dt / 16)
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
  return uuid
}

export function debounce(func, timeout = 300) {
  let timer
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, timeout)
  }
}

export const acceptExtensions = {
  'image/*': ['.png', '.jpg', '.jpeg', '.gif','.heic']
}

export const toLowerCaseString = (str) => {
  if (typeof str === 'string') {
    return str.toLowerCase()
  }
  return ''
}

// Function to retrieve user role from local storage
export function getUserRole() {
  // Retrieve and parse the stored data
  const persistedData = JSON.parse(sessionStorage.getItem('persist:userInfo'))
  if (!persistedData?.user) {
    return
  }
  // Parse the user JSON string within the persisted data
  const user = persistedData ? JSON.parse(persistedData.user) : null

  // Return the user role
  return user?.role_name
}

// Utility to check if the user has access
export function hasAccess(permission, userRole) {
  const rolePermissions = permissions[userRole]
  return rolePermissions && rolePermissions[permission]
}

export const convertToCalenderDate = (timestamp) => {
  return dayjs.unix(timestamp).format('M/D/YYYY')
}

export const getFormattedDateFromTimestamp = (input, format = 'YYYY-MM-DDTHH:mm:ss') => {
  // Check if the input is a valid timestamp (a valid numeric value).
  const timestamp = Number(input)
  if (!isNaN(timestamp) && timestamp > 0) {
    return dayjs.unix(timestamp).format(format) // Use dayjs.unix() for timestamps in seconds
  }
  return null // Return null if input is not a valid timestamp
}

export const getMinDate = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0') // Add leading zero
  const day = String(today.getDate()).padStart(2, '0') // Add leading zero
  const hours = String(today.getHours()).padStart(2, '0')
  const minutes = String(today.getMinutes()).padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

export const getISOToUTCDateFromTimestamp = (timestamp) => {
  return dayjs.unix(timestamp)
}

export const capitalizeAndRemoveUnderscore = (str) => {
  const newStr =
    str &&
    str
      .toLowerCase() // Convert the entire string to lowercase
      .replace(/_/g, ' ') // Replace underscores with spaces
      .split(' ') // Split the string into an array of words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
      .join(' ') // Join the words back into a single string
  return newStr
}

export const setTimestamp = (timeZone) => {
  return dayjs().tz(timeZone).unix().toString()
}

export const getTimeStamp = (dateString) => {
  // If dateString is null than emmpty that value
  if (dateString === 'NaN') {
    return ''
  }
  // Create a dayjs object in UTC
  const date = dayjs(dateString)

  // Get the UTC timestamp in seconds
  const utcTimestampInSeconds = date.unix().toString()

  return utcTimestampInSeconds
}

// Convert timestamp to date format (19th Sep 2024)
export const convertTimestampToDate = (timestamp, format) => {
  return dayjs(timestamp * 1000).format(format || 'DD MMM YYYY') // Multiply by 1000 to convert from seconds to milliseconds
}

// Convert timestamp to time format (01:11 AM)
export const convertTimestampToTime = (timestamp) => {
  if (timestamp === 'NaN') {
    return ''
  }
  return dayjs(timestamp * 1000).format('hh:mm A')
}

// Convert timestamp to Date object
export const convertUnixToDate = (unixTimestamp) => new Date(unixTimestamp * 1000)

export const isTimestampSmallerThanCurrent = (givenTimestamp) => {
  const currentTimestamp = Math.floor(Date.now() / 1000) // Current timestamp in seconds
  return givenTimestamp < currentTimestamp
}

// Convert timestamp to date format (YYYY-MM-DD)
export const convertTimestampToCalenderDate = (timestamp, format) => {
  if (format) {
    return dayjs(timestamp * 1000).format('YYYY-MM-DD')
  } else {
    return dayjs(timestamp * 1000)
  } // Multiply by 1000 to convert from seconds to milliseconds
}

// Get user's local timezone
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Convert timestamp to date with timezone
export const convertTimestampToDateWithTimezone = (timestamp, tz, format) => {
  if (!timestamp) return ''
  const timezone = tz || getUserTimezone()
  return dayjs(timestamp * 1000).tz(timezone).format(format || 'DD MMM YYYY')
}

// Convert timestamp to time with timezone
export const convertTimestampToTimeWithTimezone = (timestamp, tz) => {
  if (!timestamp || timestamp === 'NaN') return ''
  const timezone = tz || getUserTimezone()
  return dayjs(timestamp * 1000).tz(timezone).format('hh:mm A')
}
// Utility Functions
export const formatPhoneNumbertoString = (phoneNumber) => {
  return phoneNumber.replace(/\D/g, '') // Remove non-numeric characters
}

export const maskPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return ''
  const cleaned = formatPhoneNumbertoString(phoneNumber)
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`
  }
  return phoneNumber
}

export const convertTimeToLocalTime = (timestamp) => {
  return dayjs(timestamp * 1000).format('hh:mm A')
}

export const formatTimeAgo = (timestamp) => {
  const now = new Date()
  const time = new Date(timestamp * 1000) // Convert seconds to milliseconds
  const diffInSeconds = Math.floor((now - time) / 1000)
  if (diffInSeconds < 60) {
    return `${diffInSeconds} sec ago`
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return `${minutes} min ago`
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return `${hours} h ago`
  } else {
    const days = Math.floor(diffInSeconds / 86400)
    return `${days} day${days > 1 ? 's' : ''} ago`
  }
}

//view log date display
export const logFieldConvertor = (key, value, tz) => {
  const dateFields = []
  const timeFields = ['']
  const dateTimeFields = [
    'booking_date_time',
    'updated_at',
    'created_at',
    'started_cleaning_at',
    'ended_cleaning_at',
    'next_checkout_time',
    'next_checkin_time',
    'airbnb_checkout_time',
    'airbnb_checkin_time'
  ]

  if (dateFields?.includes(key)) {
    return value ? convertTimestampToDateWithTimezone(value, tz) : null
  } else if (timeFields?.includes(key)) {
    return value ? convertTimestampToTimeWithTimezone(value, tz) : null
  } else if (dateTimeFields?.includes(key)) {
    return `${convertTimestampToDateWithTimezone(value, tz)} - ${convertTimestampToTimeWithTimezone(value, tz)}`
  } else if (typeof value === 'boolean') {
    return value ? 'True' : 'False'
  } else {
    return value
  }
}

export const generateNumberPattern = (totalCount, base) => {
  const result = []
  for (let i = 0; i < totalCount; i++) {
    result.push(i % base)
  }

  return result
}

export const getWindowDivSize = () => {
  const desktopSize = window.matchMedia('(min-width: 600px)')?.matches

  return desktopSize ? 2 : 1
}

export const appendImageURL = (img) => {
  if (img) {
    if (img.includes('/static/media')) {
      return img
    } else if (img.startsWith('https://s3.ap-south-1.amazonaws.com')) {
      return img
    } else {
      // eslint-disable-next-line no-undef
      return `${process.env.REACT_APP_IMAGE_URL}${img}`
    }
  }
  return ''
}

export const getLocationAddress = async (latitude, longitude) => {
  try {
    const apiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY
    const url = 'https://maps.googleapis.com/maps/api/geocode/json'
    const response = await axios.get(url, {
      params: {
        latlng: `${latitude},${longitude}`,
        key: apiKey
      }
    })
    if (response.data.results.length > 0) {
      return { address: response.data.results[0].formatted_address, placeId: response?.data?.results[0]?.place_id }
    } else {
      return null
    }
  } catch (error) {
    console.error('Error fetching address:', error)
    return null
  }
}

// export const getPlaceDetailsById = async (place_id) => {
//   try {
//     const apiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY
//     const url = `https://maps.googleapis.com/maps/api/place/details/json?placeid=${place_id}&key=${apiKey}`
//     const response = await axios.get(url)
//     if (response.data?.result?.url) {
//       return response.data?.result?.url
//     } else {
//       return null
//     }
//   } catch (error) {
//     console.error('Error fetching address:', error)
//     return null
//   }
// }

export const disableDate = () => {
  return new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0]
}

export const getDayStartAndEndTimestamps = (timestamp) => {
  // Convert the Unix timestamp (in seconds) to a Day.js object
  const givenDate = dayjs.unix(timestamp)

  // Calculate the start and end of the day (UTC)
  const startOfDayTimestamp = givenDate.startOf('day').unix() // Start of day in seconds (Unix timestamp)
  const endOfDayTimestamp = givenDate.endOf('day').unix() // End of day in seconds (Unix timestamp)

  return {
    startOfDayTimestamp,
    endOfDayTimestamp
  }
}

export const hideMorePopover = () => {
  const popover = document.querySelector('.fc-more-popover')
  if (popover) {
    popover.style.display = 'none'
  }
}

export const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== 'string') return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const formatAmount = (amount) => {
  if (isNaN(amount)) {
    return amount
  }
  return `$${Number(amount).toFixed(2)}`
}

export const getDefaultTime = (hours) => {
  let date = new Date()
  date.getHours() >= 11 ? date.setDate(date.getDate() + 1) : date
  date.setHours(hours, 0, 0, 0)
  const unixTimestamp = Math.floor(date.getTime() / 1000)
  return unixTimestamp
}

export const removeLeadingZeros = (input) => {
  const str = input.toString()
  const result = str.replace(/^0+/, '')
  return result === '' ? '' : result
}

export const isDateBefore = (date, referenceDate) => (referenceDate ? date < referenceDate : false)
export const isDateAfter = (date, referenceDate) => (referenceDate ? date > referenceDate : false)

export const formatCleaningTime = (time) => {
  const [hours, minutes, seconds] = time.split(':').map(Number)
  let result = []

  if (hours > 0) result.push(`${hours}h`)
  if (minutes > 0) result.push(`${minutes}m`)
  if (seconds > 0) result.push(`${seconds}s`)
  return `${result.join(' ')}` || '--'
}

export const formatCustomTrim = (value) => {
  if (typeof value !== 'string') return value
  return value.replace(/^\s+|\s+$/g, '')
}

export const getBookingStatusClass = (status) => {
  switch (status) {
    case 'scheduled':
      return 'sky_bg'
    case 'in progress':
      return 'yellow_bg'
    case 'canceled':
    case 'canceled cleaner':
      return 'red_bg'
    case 'completed':
      return 'bg-success-light'
    default:
      return 'sky_bg'
  }
}

export const checkIfPastDate = (timestamp, isBookingDate) => {
  if (isBookingDate) {
    const bookingDate = moment.unix(timestamp).startOf('day')
    const currentDate = moment().startOf('day')
    return currentDate.isAfter(bookingDate)
  } else {
    const currentDate = moment()
    const dateToCheck = timestamp.toString().length === 10 ? moment.unix(timestamp) : moment(timestamp)
    return dateToCheck.isBefore(currentDate)
  }
}

// ─── Timezone-aware booking helpers (PR 2 — frontend admin) ──────────────

/**
 * Returns a dayjs object representing the booking's wall-clock time in
 * the property's timezone. Prefers `booking_wall_clock` (new field) and
 * falls back to `booking_date_time` (legacy unix timestamp string).
 *
 * @param {object} schedule - PropertySchedule object from API
 * @param {string} propertyTimezone - IANA timezone (e.g. 'America/Los_Angeles')
 * @returns {dayjs.Dayjs|null} dayjs anchored in the property's tz, or null
 */
export const getBookingDayjs = (schedule, propertyTimezone) => {
  if (!schedule) return null
  const tz = propertyTimezone || getUserTimezone()
  if (schedule.booking_wall_clock) {
    // wall-clock string already in property's tz; parse as local in that tz
    return dayjs.tz(schedule.booking_wall_clock, tz)
  }
  if (schedule.booking_date_time) {
    return dayjs.unix(Number(schedule.booking_date_time)).tz(tz)
  }
  return null
}

/**
 * Formats the booking date+time for display in the property's timezone.
 * Use this anywhere we currently render booking_date_time.
 *
 * @param {object} schedule - PropertySchedule
 * @param {string} propertyTimezone - IANA timezone
 * @param {string} [format] - dayjs format (default 'DD MMM YYYY hh:mm A')
 * @returns {string}
 */
export const formatBookingForDisplay = (schedule, propertyTimezone, format) => {
  const d = getBookingDayjs(schedule, propertyTimezone)
  if (!d) return ''
  return d.format(format || 'DD MMM YYYY hh:mm A')
}

/**
 * Extracts a wall-clock string ('YYYY-MM-DDTHH:mm:ss') from a dayjs picker
 * value, interpreted in the property's timezone. Use this when submitting
 * to the backend POST/PATCH /property-schedule.
 *
 * @param {dayjs.Dayjs|Date|string|number} value - dayjs from the picker
 * @param {string} propertyTimezone - IANA timezone
 * @returns {string} wall-clock string, e.g. '2026-11-11T11:00:00'
 */
export const dayjsToWallClock = (value, propertyTimezone) => {
  if (!value) return null
  const tz = propertyTimezone || getUserTimezone()
  // Reinterpret the wall-clock the user picked as if it were in property tz
  // (the picker shows wall-clock numbers; we just need to format them).
  const d = dayjs.isDayjs(value) ? value : dayjs(value)
  return d.tz(tz).format('YYYY-MM-DDTHH:mm:ss')
}
